import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Calendar, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';

const CALENDLY_URL = "https://calendly.com/yaenlineasoft/30min";

const benefits = [
  'Free 30-minute consultation',
  'No obligation to proceed',
  'Get a project estimate',
  'Discuss your requirements',
];

const CTASection = () => {
  return (
    <section
      id="cta"
      className="relative py-20 md:py-32 overflow-hidden"
      data-testid="cta-section"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-[#030305]" />
      
      {/* CTA Gradient */}
      <div className="absolute inset-0 cta-gradient" />
      
      {/* Grid pattern */}
      <div className="absolute inset-0 grid-bg opacity-30" />

      <div className="relative max-w-5xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="relative"
        >
          {/* Glowing border effect */}
          <div className="absolute -inset-px bg-gradient-to-r from-violet-600 to-cyan-500 rounded-3xl blur-sm opacity-50" />
          
          <div className="relative bg-[#0a0a0a] rounded-3xl border border-white/10 p-8 md:p-16 text-center overflow-hidden">
            {/* Decorative elements */}
            <div className="absolute top-0 left-0 w-40 h-40 bg-violet-600/20 rounded-full blur-[100px]" />
            <div className="absolute bottom-0 right-0 w-40 h-40 bg-cyan-500/20 rounded-full blur-[100px]" />

            <div className="relative z-10">
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-violet-600/20 text-violet-400 text-sm font-mono mb-6">
                  <Calendar className="w-4 h-4" />
                  SCHEDULE YOUR CALL TODAY
                </span>
              </motion.div>

              <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-6" data-testid="cta-heading">
                Ready to Transform{' '}
                <span className="gradient-text">Your Business?</span>
              </h2>

              <p className="text-gray-400 text-base md:text-lg max-w-2xl mx-auto mb-8">
                Let's discuss how custom software can help you streamline operations, 
                increase efficiency, and drive growth. Book your free consultation now.
              </p>

              {/* Benefits list */}
              <div className="flex flex-wrap justify-center gap-4 mb-10">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-2 text-gray-300 text-sm">
                    <CheckCircle className="w-4 h-4 text-cyan-400" />
                    <span>{benefit}</span>
                  </div>
                ))}
              </div>

              {/* CTA Button */}
              <Button
                asChild
                size="lg"
                className="bg-gradient-to-r from-violet-600 to-cyan-500 hover:from-violet-500 hover:to-cyan-400 text-white rounded-full px-10 py-7 text-lg font-bold transition-all shadow-[0_0_30px_rgba(124,58,237,0.4)] hover:shadow-[0_0_50px_rgba(124,58,237,0.6)] hover:scale-105 btn-shine group"
                data-testid="cta-button"
              >
                <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
                  <Calendar className="mr-2 h-5 w-5" />
                  Schedule a Call
                  <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                </a>
              </Button>

              <p className="text-gray-500 text-sm mt-6">
                Or email us at{' '}
                <a 
                  href="mailto:support@yaenlineasoft.com" 
                  className="text-violet-400 hover:text-violet-300 transition-colors"
                  data-testid="cta-email-link"
                >
                  support@yaenlineasoft.com
                </a>
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CTASection;
